import { ref } from 'vue'
import type { Ref } from 'vue'
import type { WizardField, StoreWizardFieldPayload, UpdateWizardFieldPayload } from '@/types/wizard-forms'
import {
  listWizardFields as apiListWizardFields,
  createWizardField as apiCreateWizardField,
  updateWizardField as apiUpdateWizardField,
  deleteWizardField as apiDeleteWizardField,
} from '@/api/wizard-forms'

const fields = ref<WizardField[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

async function fetchFields(formId: number, pageId: number): Promise<void> {
  loading.value = true
  error.value = null
  try {
    fields.value = await apiListWizardFields(formId, pageId)
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function createField(
  formId: number,
  pageId: number,
  payload: StoreWizardFieldPayload,
): Promise<WizardField> {
  const created = await apiCreateWizardField(formId, pageId, payload)
  fields.value = [...fields.value, created]
  return created
}

async function updateField(
  formId: number,
  pageId: number,
  fieldId: number,
  payload: UpdateWizardFieldPayload,
): Promise<WizardField> {
  const updated = await apiUpdateWizardField(formId, pageId, fieldId, payload)
  fields.value = fields.value.map((f) => (f.id === fieldId ? updated : f))
  return updated
}

async function deleteField(formId: number, pageId: number, fieldId: number): Promise<void> {
  await apiDeleteWizardField(formId, pageId, fieldId)
  fields.value = fields.value.filter((f) => f.id !== fieldId)
}

export function useWizardFields(): {
  fields: Ref<WizardField[]>
  loading: Ref<boolean>
  error: Ref<string | null>
  fetchFields: (formId: number, pageId: number) => Promise<void>
  createField: (formId: number, pageId: number, payload: StoreWizardFieldPayload) => Promise<WizardField>
  updateField: (formId: number, pageId: number, fieldId: number, payload: UpdateWizardFieldPayload) => Promise<WizardField>
  deleteField: (formId: number, pageId: number, fieldId: number) => Promise<void>
} {
  return {
    fields,
    loading,
    error,
    fetchFields,
    createField,
    updateField,
    deleteField,
  }
}
